import React, { useState, useEffect, useCallback, useRef } from 'react';
import './index.css';
import html2canvas from 'html2canvas';
import { AuthProvider, useAuth } from './AuthContext';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SetupWizard from './pages/SetupWizard';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import OwnerAdminLogin from './pages/OwnerAdminLogin';
import OwnerAdmin from './pages/OwnerAdmin';
import Dashboard from './pages/Dashboard';
import TimeTracker from './pages/TimeTracker';
import TeamView from './pages/TeamView';
import Employees from './pages/Employees';
import Screenshots from './pages/Screenshots';
import Activity from './pages/Activity';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import Profile from './pages/Profile';
import useActivityMonitor from './hooks/useActivityMonitor';
import { getActiveEntry, getSettings, uploadScreenshot } from './api';

const pages = {
  dashboard: Dashboard, clock: TimeTracker, team: TeamView, employees: Employees,
  screenshots: Screenshots, activity: Activity, reports: Reports, settings: Settings,
  billing: Billing, profile: Profile,
};

// Detect if we're on a special public route via URL hash or path
function detectPublicRoute() {
  const path = window.location.pathname;
  const search = window.location.search;
  if (path === '/verify-email' || search.includes('verify-email')) return 'verify-email';
  if (path === '/reset-password' || search.includes('reset-password')) return 'reset-password';
  if (path === '/accept-invite' || search.includes('accept-invite')) return 'accept-invite';
  if (path === '/owner-admin' || path.startsWith('/owner-admin')) return 'owner-admin';
  if (path === '/signup') return 'signup';
  if (path === '/forgot-password') return 'forgot-password';
  // Support hash-based routing too
  const hash = window.location.hash;
  if (hash === '#/signup') return 'signup';
  if (hash === '#/verify-email') return 'verify-email';
  if (hash === '#/reset-password') return 'reset-password';
  if (hash === '#/forgot-password') return 'forgot-password';
  // Check URL query params for token (verify or reset)
  const params = new URLSearchParams(search);
  if (params.get('token') && path.includes('verify')) return 'verify-email';
  if (params.get('token') && path.includes('reset')) return 'reset-password';
  if (params.get('token') && path.includes('invite')) return 'accept-invite';
  return null;
}

function AppInner() {
  const { user, updateUser } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [authView, setAuthView] = useState(() => detectPublicRoute() || 'login'); // login | signup | forgot | reset | verify
  const [adminAuthed, setAdminAuthed] = useState(() => !!localStorage.getItem('hrtrack_admin_token'));
  const [activeEntryId, setActiveEntryId] = useState(null);
  const [branding, setBranding] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);

  const captureIntervalRef = useRef(null);
  const captureEntryIdRef = useRef(null);

  // Check for Stripe return params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      setPage('billing');
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const loadBranding = useCallback(async () => {
    try {
      const s = await getSettings();
      setBranding(s);
      document.documentElement.style.setProperty('--primary', s.primary_color || '#4f46e5');
      document.documentElement.style.setProperty('--accent', s.accent_color || '#06b6d4');
    } catch {}
  }, []);

  useEffect(() => {
    if (user) {
      loadBranding();
      window.__brandingUpdated = loadBranding;
    }
  }, [user, loadBranding]);

  useEffect(() => {
    if (!user?.employee_id) return;
    const retryPending = async () => {
      const pending = JSON.parse(localStorage.getItem('pending_clockout') || '[]');
      if (!pending.length) return;
      const remaining = [];
      for (const p of pending) {
        try {
          const { clockOut } = await import('./api');
          await clockOut(p.employee_id);
        } catch { remaining.push(p); }
      }
      localStorage.setItem('pending_clockout', JSON.stringify(remaining));
    };
    retryPending();
  }, [user?.employee_id]);

  useEffect(() => {
    if (!user?.employee_id) return;
    const check = async () => {
      const entry = await getActiveEntry(user.employee_id).catch(() => null);
      setActiveEntryId(entry?.id || null);
    };
    check();
    const t = setInterval(check, 60000);
    window.__refreshActiveEntry = check;
    return () => { clearInterval(t); window.__refreshActiveEntry = null; };
  }, [user?.employee_id]);

  const { getActivityScore } = useActivityMonitor({
    employeeId: user?.employee_id,
    timeEntryId: activeEntryId,
    enabled: !!user?.employee_id && !!activeEntryId,
    idleThresholdMs: 120000,
  });

  useEffect(() => { window.__getActivityScore = getActivityScore; }, [getActivityScore]);

  const doCapture = useCallback(async () => {
    const entryId = captureEntryIdRef.current;
    const empId = user?.employee_id;
    if (!empId) return;
    try {
      const canvas = await html2canvas(document.body, { useCORS: true, logging: false, scale: 0.75 });
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const fd = new FormData();
        fd.append('screenshot', blob, 'auto_capture.png');
        fd.append('employee_id', empId);
        if (entryId) fd.append('time_entry_id', entryId);
        const score = window.__getActivityScore ? window.__getActivityScore(300000) : 0;
        fd.append('activity_score', String(score));
        try { await uploadScreenshot(fd); } catch {}
      }, 'image/png');
    } catch {}
  }, [user?.employee_id]);

  const stopAutoCapture = useCallback(() => {
    clearInterval(captureIntervalRef.current);
    captureIntervalRef.current = null;
    captureEntryIdRef.current = null;
    setIsCapturing(false);
  }, []);

  useEffect(() => {
    window.__startAutoCapture = async (entryId) => {
      try {
        const settings = await getSettings();
        if (settings.screenshot_enabled !== 'true') return;
        const intervalMins = parseInt(settings.screenshot_interval_mins) || 5;
        captureEntryIdRef.current = entryId;
        setIsCapturing(true);
        setTimeout(async () => {
          await doCapture();
          captureIntervalRef.current = setInterval(doCapture, intervalMins * 60 * 1000);
        }, 2000);
      } catch (e) { console.info('Auto capture setup failed:', e.message); }
    };
    window.__stopAutoCapture = stopAutoCapture;
  }, [doCapture, stopAutoCapture]);

  useEffect(() => {
    if (!activeEntryId && isCapturing) stopAutoCapture();
  }, [activeEntryId, isCapturing, stopAutoCapture]);

  // ── Public routes (no auth needed) ────────────────────────────────────────
  if (authView === 'verify-email' && user?.email_verified === false) {
    return <VerifyEmail user={user} onVerified={(data) => { updateUser(data.user); setAuthView('app'); }} />;
  }
  if (authView === 'verify-email' && !user) {
    return <VerifyEmail user={null} onVerified={(data) => { updateUser(data.user); setAuthView('app'); }} />;
  }
  if (authView === 'reset-password') {
    return <ResetPassword goToLogin={() => setAuthView('login')} />;
  }
  if (authView === 'accept-invite') {
    return <AcceptInvite onAccepted={(data) => { updateUser(data.user); setAuthView('app'); }} />;
  }
  if (authView === 'owner-admin') {
    if (!adminAuthed) {
      return <OwnerAdminLogin onLogin={() => { setAdminAuthed(true); }} />;
    }
    return <OwnerAdmin onLogout={() => { localStorage.removeItem('hrtrack_admin_token'); setAdminAuthed(false); }} />;
  }
  if (authView === 'forgot') {
    return <ForgotPassword goToLogin={() => setAuthView('login')} />;
  }

  // ── Not logged in ─────────────────────────────────────────────────────────
  if (!user) {
    if (authView === 'signup') {
      return <Signup
        onSuccess={(data) => {
          updateUser(data.user);
          setAuthView(data.user.email_verified ? 'app' : 'verify-email');
        }}
        goToLogin={() => setAuthView('login')}
      />;
    }
    return <Login
      goToSignup={() => setAuthView('signup')}
      goToForgotPassword={() => setAuthView('forgot')}
    />;
  }

  // ── Logged in but email not verified ──────────────────────────────────────
  if (!user.email_verified) {
    return <VerifyEmail
      user={user}
      onVerified={(data) => { updateUser(data.user); }}
    />;
  }

  // ── Logged in, verified, but setup not complete ───────────────────────────
  if (user.workspace && !user.workspace.setup_completed && user.role === 'admin') {
    return <SetupWizard user={user} onComplete={() => {
      const updated = { ...user, workspace: { ...user.workspace, setup_completed: 1 } };
      updateUser(updated);
    }} />;
  }

  // ── Main App ──────────────────────────────────────────────────────────────
  const role = user?.role || 'employee';
  const allowedPages = {
    admin: ['dashboard', 'clock', 'team', 'employees', 'screenshots', 'activity', 'reports', 'settings', 'billing', 'profile'],
    manager: ['dashboard', 'clock', 'team', 'screenshots', 'activity', 'reports', 'profile'],
    employee: ['dashboard', 'clock', 'screenshots', 'activity', 'reports', 'profile'],
  };
  const allowed = allowedPages[role] || allowedPages.employee;
  const effectivePage = allowed.includes(page) ? page : 'dashboard';
  const Page = pages[effectivePage] || Dashboard;

  return (
    <div className="layout">
      <Sidebar page={effectivePage} setPage={setPage} branding={branding} isCapturing={isCapturing} activeEntryId={activeEntryId} />
      <main className="main-content">
        <Page />
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
